import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    addDays,
    addMonths,
    endOfMonth,
    endOfWeek,
    format,
    isWithinInterval,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import apiClient from '../../services/api';

interface Timesheet {
    id: string;
    shiftDate: string;
    startTime: string;
    endTime: string;
    totalHours: number;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    rejectionReason?: string;
}

const statusConfig = {
    APPROVED: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    REJECTED: { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-200' },
    PENDING:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-700 border-amber-200' },
};

type PeriodMode = 'week' | 'fortnight' | 'month';

const MyTimesheets = () => {
    const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
    const [loading, setLoading] = useState(true);
    const [periodMode, setPeriodMode] = useState<PeriodMode>('week');
    const [anchorDate, setAnchorDate] = useState(new Date());

    useEffect(() => {
        apiClient.get('/timesheets/my')
            .then(r => setTimesheets(r.data.data ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const getPeriodRange = () => {
        if (periodMode === 'month') {
            return { start: startOfMonth(anchorDate), end: endOfMonth(anchorDate) };
        }

        const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
        return {
            start,
            end: periodMode === 'fortnight' ? endOfWeek(addDays(start, 7), { weekStartsOn: 1 }) : endOfWeek(anchorDate, { weekStartsOn: 1 }),
        };
    };

    const { start: periodStart, end: periodEnd } = getPeriodRange();
    const periodTimesheets = timesheets
        .filter(ts => isWithinInterval(new Date(ts.shiftDate), { start: periodStart, end: periodEnd }))
        .sort((a, b) => new Date(b.shiftDate).getTime() - new Date(a.shiftDate).getTime());
    const totalApproved = periodTimesheets.filter(t => t.status === 'APPROVED').reduce((s, t) => s + t.totalHours, 0);
    const pending = periodTimesheets.filter(t => t.status === 'PENDING').length;
    const periodLabel = `${format(periodStart, 'd MMM')} - ${format(periodEnd, periodStart.getFullYear() === periodEnd.getFullYear() ? 'd MMM yyyy' : 'd MMM yyyy')}`;

    const movePeriod = (direction: -1 | 1) => {
        if (periodMode === 'month') {
            setAnchorDate(prev => direction === -1 ? subMonths(prev, 1) : addMonths(prev, 1));
            return;
        }
        setAnchorDate(prev => direction === -1
            ? subDays(prev, periodMode === 'fortnight' ? 14 : 7)
            : addDays(prev, periodMode === 'fortnight' ? 14 : 7)
        );
    };

    if (loading) {
        return (
            <div className="p-6 space-y-3 max-w-2xl mx-auto">
                {[1,2,3].map(i => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
                        <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
                        <div className="h-3 bg-slate-100 rounded w-1/2" />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">My Timesheets</h1>
                    <p className="text-sm text-slate-400 mt-0.5">Track your submitted hours by period</p>
                </div>
                <Link
                    to="/timesheets/submit"
                    className="flex-shrink-0 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                >
                    + Submit New
                </Link>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3">
                <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-lg">
                    {(['week', 'fortnight', 'month'] as PeriodMode[]).map(mode => (
                        <button
                            key={mode}
                            onClick={() => setPeriodMode(mode)}
                            className={`px-2 py-2 rounded-md text-xs font-bold capitalize ${periodMode === mode ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}
                        >
                            {mode === 'fortnight' ? '2 weeks' : mode}
                        </button>
                    ))}
                </div>
                <div className="flex items-center justify-between gap-2">
                    <button onClick={() => movePeriod(-1)} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Previous period">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => setAnchorDate(new Date())} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        {periodLabel}
                    </button>
                    <button onClick={() => movePeriod(1)} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Next period">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Summary strip */}
            {periodTimesheets.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-slate-800">{periodTimesheets.length}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Total</div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-emerald-700">{totalApproved.toFixed(1)}h</div>
                        <div className="text-xs text-emerald-600 mt-0.5">Approved hrs</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-amber-700">{pending}</div>
                        <div className="text-xs text-amber-600 mt-0.5">Pending</div>
                    </div>
                </div>
            )}

            {/* Cards */}
            {periodTimesheets.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                        </svg>
                    </div>
                    <p className="text-slate-600 font-medium">No timesheets in this period</p>
                    <p className="text-sm text-slate-400 mt-1">Use the period controls above to review another week, 2 weeks, or month.</p>
                    {timesheets.length === 0 && (
                        <Link to="/timesheets/submit" className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors">
                            Submit Timesheet
                        </Link>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {periodTimesheets.map(ts => {
                        const cfg = statusConfig[ts.status] ?? statusConfig.PENDING;
                        const date = new Date(ts.shiftDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                        const start = new Date(ts.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        const end = new Date(ts.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        return (
                            <div key={ts.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4">
                                <div className="w-1 self-stretch rounded-full flex-shrink-0"
                                    style={{ background: ts.status === 'APPROVED' ? '#10b981' : ts.status === 'REJECTED' ? '#ef4444' : '#f59e0b' }} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="font-semibold text-slate-800 text-sm">{date}</span>
                                        <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${cfg.cls}`}>{cfg.label}</span>
                                    </div>
                                    <div className="text-sm text-slate-500 mt-0.5">{start} – {end}</div>
                                    {ts.status === 'REJECTED' && ts.rejectionReason && (
                                        <div className="text-xs text-red-600 mt-1 bg-red-50 rounded px-2 py-1">{ts.rejectionReason}</div>
                                    )}
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className="text-lg font-bold text-slate-800">{ts.totalHours}h</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyTimesheets;
